import React, { useCallback, useEffect, useMemo, useState } from "react";
import CachedIcon from "@mui/icons-material/Cached";
import {
  Avatar,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import {
  getAllSettlements,
  markWithdrawalPaid,
  payoutWithdrawal,
  rejectWithdrawal,
} from "../../services/api/consultantsPayout";

const inr = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

// Pending consultant-initiated withdrawal requests. Money is already held
// (debited at request time); the admin transfers it out then marks paid.
export default function WithdrawalRequests() {
  const [loader, setLoader] = useState(true);
  const [rows, setRows] = useState([]);
  const [reload, setReload] = useState(true);
  const [busyId, setBusyId] = useState(null);

  // Mark-paid dialog owns its own UTR field.
  const [payTarget, setPayTarget] = useState(null);
  const [utr, setUtr] = useState("");
  const [rejectTarget, setRejectTarget] = useState(null);
  const [reason, setReason] = useState("");

  const fetchList = async () => {
    setLoader(true);
    try {
      // Server returns all settlements; the pending ones are the open requests.
      const response = await getAllSettlements({ limit: 200 });
      const list = response?.data?.data?.data || [];
      // Pending = actionable; processing = RazorpayX payout in flight (read-only).
      setRows(list.filter((r) => r.status === "pending" || r.status === "processing"));
    } catch {
      setRows([]);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [reload]);

  const closePay = useCallback(() => {
    setPayTarget(null);
    setUtr("");
  }, []);
  const closeReject = useCallback(() => {
    setRejectTarget(null);
    setReason("");
  }, []);

  const confirmPaid = async () => {
    if (!payTarget) return;
    setBusyId(payTarget.id);
    try {
      await markWithdrawalPaid({
        settlement_id: payTarget.id,
        reference_id: utr.trim(),
      });
      closePay();
      setReload((p) => !p);
    } catch {
      /* surfaced via interceptor toast */
    } finally {
      setBusyId(null);
    }
  };

  const payViaRazorpay = async (row) => {
    setBusyId(row.id);
    try {
      await payoutWithdrawal({ settlement_id: row.id });
      setReload((p) => !p);
    } catch {
      /* surfaced via interceptor toast */
    } finally {
      setBusyId(null);
    }
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    setBusyId(rejectTarget.id);
    try {
      await rejectWithdrawal({
        settlement_id: rejectTarget.id,
        reason: reason.trim(),
      });
      closeReject();
      setReload((p) => !p);
    } catch {
      /* surfaced via interceptor toast */
    } finally {
      setBusyId(null);
    }
  };

  const total = useMemo(
    () => rows.reduce((acc, r) => acc + Number(r.amount || 0), 0),
    [rows],
  );

  return (
    <>
      <div className="payout-toolbar">
        <div className="payout-field">
          <label>Pending Requests</label>
          <span className="payout-balance">{rows.length}</span>
        </div>
        <div className="payout-field">
          <label>Total to Pay</label>
          <span className="payout-balance">{inr(total)}</span>
        </div>
        <button
          className="payout-reload"
          onClick={() => setReload((p) => !p)}
          title="Reload"
        >
          <CachedIcon />
        </button>
      </div>

      <div className="payout-table-wrap">
        {loader ? (
          <div className="payout-loader">
            <CircularProgress size={28} />
          </div>
        ) : rows.length === 0 ? (
          <div className="payout-empty">No pending withdrawal requests.</div>
        ) : (
          <TableContainer component={Paper} className="payout-table">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Consultant</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Destination</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => {
                  const dest = r.upi_id
                    ? `UPI: ${r.upi_id}`
                    : r.account_number
                      ? `${r.bank_name || "Bank"} ••${String(
                          r.account_number,
                        ).slice(-4)} / ${r.ifsc || "-"}`
                      : "-";
                  return (
                    <TableRow key={r.id || i}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>
                        <div className="payout-party">
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {(r.consultant_name || "?").slice(0, 1).toUpperCase()}
                          </Avatar>
                          <div>
                            <div className="payout-party-name">
                              {r.consultant_name || "-"}
                            </div>
                            <code className="payout-cid">{r.consultant_id}</code>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="payout-contact">
                          <div>{r.consultant_email || "-"}</div>
                          <div className="payout-phone">
                            {r.consultant_phone || "-"}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell align="right">
                        <span className="payout-balance">{inr(r.amount)}</span>
                      </TableCell>
                      <TableCell>{dest}</TableCell>
                      <TableCell>
                        {r.created_at
                          ? new Date(r.created_at).toLocaleString("en-IN")
                          : "-"}
                      </TableCell>
                      <TableCell align="center">
                        {r.status === "processing" ? (
                          <Chip
                            label="Payout processing…"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        ) : (
                        <div
                          style={{
                            display: "flex",
                            gap: 6,
                            justifyContent: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <Button
                            variant="contained"
                            size="small"
                            color="primary"
                            disabled={busyId === r.id}
                            onClick={() => payViaRazorpay(r)}
                            sx={{ textTransform: "none", fontWeight: 600 }}
                          >
                            {busyId === r.id ? "Paying…" : "Pay via RazorpayX"}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            color="success"
                            disabled={busyId === r.id}
                            onClick={() => setPayTarget(r)}
                            sx={{ textTransform: "none" }}
                          >
                            Mark Paid
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            disabled={busyId === r.id}
                            onClick={() => setRejectTarget(r)}
                            sx={{ textTransform: "none" }}
                          >
                            Reject
                          </Button>
                        </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>

      {/* Mark paid — capture the bank/UPI reference (UTR) for reconciliation. */}
      <Dialog open={!!payTarget} onClose={closePay} maxWidth="xs" fullWidth>
        <DialogTitle>Mark Withdrawal Paid</DialogTitle>
        <DialogContent>
          <p style={{ margin: "4px 0 16px" }}>
            Confirm you have transferred{" "}
            <b>{inr(payTarget?.amount)}</b> to{" "}
            <b>{payTarget?.consultant_name}</b>. This will now deduct the amount
            from their balance.
          </p>
          <TextField
            fullWidth
            size="small"
            label="Reference / UTR (optional)"
            value={utr}
            onChange={(e) => setUtr(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closePay} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={confirmPaid}
            disabled={busyId === payTarget?.id}
            sx={{ textTransform: "none" }}
          >
            Confirm Paid
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject — credits the held amount back to the consultant. */}
      <Dialog open={!!rejectTarget} onClose={closeReject} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Withdrawal</DialogTitle>
        <DialogContent>
          <p style={{ margin: "4px 0 16px" }}>
            Reject <b>{rejectTarget?.consultant_name}</b>'s request for{" "}
            <b>{inr(rejectTarget?.amount)}</b>? Their balance is unaffected —
            the amount is only deducted when a request is marked paid.
          </p>
          <TextField
            fullWidth
            size="small"
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReject} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmReject}
            disabled={busyId === rejectTarget?.id}
            sx={{ textTransform: "none" }}
          >
            Confirm Reject
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
